import type {
  AxiosError} from 'axios';
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig
} from 'axios';
import { ApiError, type ApiErrorPayload } from './error';
import type { TokenStore } from './token-store';

export interface CreateApiClientOptions {
  baseURL: string;
  tokenStore: TokenStore;
  /** Called when refresh fails — apps wire this to "log the user out". */
  onAuthFailure?: () => void;
  /**
   * Called for 403 responses whose body `code` is permission_denied,
   * out_of_scope, account_deactivated or password_change_required. Observational: the error still
   * propagates unchanged.
   */
  onForbidden?: (error: ApiError, request?: { method: string }) => void;
  /** Backend route used to mint a new access token from a refresh token. */
  refreshPath?: string;
  /** Extra headers applied to every request. */
  defaultHeaders?: Record<string, string>;
  /** Request timeout (ms). Default: 30s. */
  timeout?: number;
}

const FORBIDDEN_CODES = new Set([
  'permission_denied',
  'out_of_scope',
  'account_deactivated',
  'password_change_required',
]);

export type ApiClient = AxiosInstance;

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/**
 * Single source of truth for HTTP requests across all FinMin apps.
 *
 * Responsibilities:
 *  - Attach the access token to every request.
 *  - On 401: try to refresh exactly once, then retry the original request.
 *  - On refresh failure: clear tokens and notify the host app (logout).
 *  - Normalize all errors to ApiError so UI never sees AxiosError directly.
 *
 * Concurrent 401s are coalesced into a single refresh promise so that a burst
 * of failed requests doesn't spam the refresh endpoint.
 */
export function createApiClient(options: CreateApiClientOptions): ApiClient {
  const {
    baseURL,
    tokenStore,
    onAuthFailure,
    onForbidden,
    refreshPath = '/api/v1/auth/refresh',
    defaultHeaders,
    timeout = 30_000,
  } = options;

  const client = axios.create({
    baseURL,
    timeout,
    headers: { 'Content-Type': 'application/json', ...defaultHeaders },
  });

  // --- Request: attach Bearer token ----------------------------------------
  client.interceptors.request.use((config) => {
    const token = tokenStore.getAccessToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // --- Response: refresh-on-401 with single-flight coalescing --------------
  let refreshPromise: Promise<string | null> | null = null;

  async function refreshAccessToken(): Promise<string | null> {
    const refreshToken = tokenStore.getRefreshToken();
    if (!refreshToken) return null;

    try {
      // Use bare axios to avoid recursing through this client's interceptors.
      // Response shape matches the backend's AuthRefreshResponse schema.
      const response = await axios.post<{
        access_token: string;
        refresh_token: string;
        firestore_token?: string | null;
      }>(
        `${baseURL}${refreshPath}`,
        { refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' }, timeout },
      );
      tokenStore.setTokens({
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token,
        firestoreToken: response.data.firestore_token ?? null,
      });
      return response.data.access_token;
    } catch {
      return null;
    }
  }

  /**
   * `responseType: 'blob'` requests (PDFs) get their JSON error body as a Blob;
   * parse it so the backend's `detail` reaches the ApiError message.
   */
  async function toApiError(error: AxiosError): Promise<ApiError> {
    const res = error.response;
    if (res?.data instanceof Blob && res.data.type.includes('json')) {
      try {
        res.data = JSON.parse(await res.data.text());
      } catch {
        // Keep the Blob; fromAxios falls back to the axios message.
      }
    }
    return ApiError.fromAxios(error as AxiosError<ApiErrorPayload>);
  }

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as RetryConfig | undefined;
      const status = error.response?.status;

      // Only attempt refresh on 401, not on the refresh endpoint itself,
      // and only once per original request.
      const isRefreshableRequest =
        status === 401 && original && !original._retry && !original.url?.endsWith(refreshPath);

      if (!isRefreshableRequest) {
        const apiError = await toApiError(error);
        if (status === 403 && apiError.code && FORBIDDEN_CODES.has(apiError.code)) {
          onForbidden?.(apiError, { method: (error.config?.method ?? 'get').toLowerCase() });
        }
        return Promise.reject(apiError);
      }

      original._retry = true;

      // Coalesce concurrent refreshes.
      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
      const newToken = await refreshPromise;

      if (!newToken) {
        tokenStore.clear();
        onAuthFailure?.();
        return Promise.reject(await toApiError(error));
      }

      original.headers.Authorization = `Bearer ${newToken}`;
      return client.request(original as AxiosRequestConfig);
    },
  );

  return client;
}
